import { ForwardedRef, forwardRef } from "react";
import {
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Radio,
  RadioGroup,
  RadioIndicator,
  RadioIcon,
  CircleIcon,
  HStack,
  Text,
  Pressable
} from "@gluestack-ui/themed";

import { Icon } from "@repo/core/components/core/Icon";
import { Button } from "@repo/core/components/core/Button";
import { Modal } from "../../Modal";
import { IModalRefProps } from "../../Modal/types";
import { ActionSheet } from "../../Actionsheet";
import { RedeemPointsModalProps } from "./types";
import { useRedeemPointsModalController } from "./controller";
import { numberToBrl } from "@repo/core/utils/currency.utils";
import { formatToBRNumber } from "@repo/core/utils/string.utils";

export const RedeemPointsModal = forwardRef(function RedeemPointsModal(
  props: RedeemPointsModalProps,
  ref: ForwardedRef<IModalRefProps>
) {
  const {
    isMobile,
    pointsList,
    selectedPoints,
    setSelectedPoints,
    handleRedeem,
    handleSkip
  } = useRedeemPointsModalController(props);

  const renderContent = () => (
    <VStack width="$full" gap="$4">
      <VStack alignItems="center" gap="$2">
        <VStack gap="$2" alignItems="center">
          <Icon name="gift" size={48} color="$primary600" />
          <Text
            textAlign="center"
            color="$textLight900"
            fontWeight={600}
            size="lg"
          >
            Resgatar Pontos
          </Text>
        </VStack>
        <Text
          textAlign="center"
          color="$textLight900"
          size="sm"
          mt="$4"
          mb="$6"
        >
          Quantos pontos deseja resgatar?
        </Text>
      </VStack>

      <RadioGroup value={selectedPoints} onChange={setSelectedPoints}>
        <VStack gap="$3">
          {pointsList.map((option) => (
            <Pressable
              borderWidth={1}
              borderColor="$borderLight200"
              borderRadius="$lg"
              bg="$backgroundLight100"
              p="$4"
              minHeight="$16"
              alignItems="center"
              justifyContent="center"
              onPress={() => setSelectedPoints(option.points.toString())}
            >
              <Radio
                key={option.points}
                value={option.points.toString()}
                w="$full"
              >
                <HStack justifyContent="space-between" flex={1}>
                  <HStack gap="$2" alignItems="center">
                    <RadioIndicator borderColor="$primary700">
                      <RadioIcon as={CircleIcon} color="$primary700" />
                    </RadioIndicator>
                    <Text color="$textLight900" fontWeight={500}>
                      {formatToBRNumber(option.points)} pts
                    </Text>
                  </HStack>

                  <Text color="$primary700" fontWeight={600}>
                    {numberToBrl(option.value)}
                  </Text>
                </HStack>
              </Radio>
            </Pressable>
          ))}
        </VStack>
      </RadioGroup>

      <VStack space="sm" width="$full" mb="$2">
        <Button
          width="$full"
          text="Resgatar pontos"
          onPress={handleRedeem}
          isDisabled={!selectedPoints}
        />
        <Button
          width="$full"
          text="Continuar sem resgatar"
          variant="outline"
          action="secondary"
          onPress={handleSkip}
        />
      </VStack>
    </VStack>
  );

  if (isMobile) {
    return (
      <ActionSheet ref={ref}>
        <VStack p="$4" w="$full">
          {renderContent()}
        </VStack>
      </ActionSheet>
    );
  }

  return (
    <Modal ref={ref}>
      <ModalHeader>
        <ModalCloseButton>
          <Icon name="times" size="xs" />
        </ModalCloseButton>
      </ModalHeader>
      <ModalBody>{renderContent()}</ModalBody>
    </Modal>
  );
});
